import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"
import { router } from "@inertiajs/react"
import { Trans, useLingui } from "@lingui/react/macro"
import { getCsrfToken } from "@/lib/csrf"
import { toast } from "@/components/ui/sonner"
import { initials } from "@/lib/initials"
import { useVoiceCallChannel } from "@/hooks/use-voice-call-channel"
import {
  SparkleIcon,
  CheckIcon,
  ResetIcon,
  ChatBubbleIcon,
  PlusIcon,
} from "./icons"
import { formatDuration } from "./call-duration"
import { AfterCallMoreSheet, type NoteVisibility } from "./after-call-more-sheet"
import { WhatsAppFollowUp } from "./after-call-whatsapp"
import { LinkDeal } from "./after-call-link-deal"
import { AfterCallTranscript } from "./after-call-transcript"
import { CallFollowUps } from "./call-follow-ups"
import type { AddActivityEmployee } from "./voice-employee"
import type {
  CallDisposition,
  CallFollowUpResponse,
  VoiceCallDeal,
  VoiceCallRecord,
} from "@/types/v2/voice"

// The approve-the-draft post-call card (CON-158). The call is
// already logged server-side; the rep's job is to glance,
// correct if the AI got it wrong, and confirm. The AI seeds the
// summary note and a follow-up checklist; the rep approves with
// Done. Renders inside the dark VoiceWidgetShell (desktop dock
// / mobile sheet) — shape branches in the shell, this content
// is viewport-agnostic.

interface AfterCallCardProps {
  voiceCall: VoiceCallRecord
  // The in-call "Quick note" jot (CON-155). Seeds the note as
  // already-edited so a late AI summary offers a swap instead
  // of clobbering what the rep wrote mid-call.
  seedNote?: string
  onClose: () => void
  // Deep-link a sub-view on mount (the dev harness uses this
  // to jump straight to the transcript). Defaults to "draft".
  initialSubview?: Subview
  // Dev-only: pre-resolved WhatsApp follow-up payload so the
  // harness can drive each follow-up branch without a live
  // fetch. Threaded straight into `WhatsAppFollowUp`.
  whatsappPreview?: CallFollowUpResponse | null
  // The rep, threaded into the shared follow-ups section so an
  // accepted Activity gets an assignee. Defaults to a minimal
  // employee whose blank id makes the server fall back to the
  // call's author (CON-197 §A5).
  currentEmployee?: AddActivityEmployee
  ownerOptions?: AddActivityEmployee[]
}

// The blank employee the card uses when no rep is threaded in:
// `assignee_id: 0` makes the activity form fall back to the
// call's author server-side.
const FALLBACK_EMPLOYEE: AddActivityEmployee = {
  id: 0,
  name: "",
  initials: null,
}

const DISPOSITIONS: CallDisposition[] = [
  "connected",
  "no_answer",
  "voicemail",
  "busy",
  "wrong_number",
]

// tone → connected (green) vs warning (amber) styling
// Labels are resolved via useDispositionLabel() inside components
// so they go through Lingui's translation pipeline.
const DISPOSITION_META: Record<
  CallDisposition,
  { tone: "connected" | "warning" }
> = {
  connected: { tone: "connected" },
  no_answer: { tone: "warning" },
  voicemail: { tone: "warning" },
  busy: { tone: "warning" },
  wrong_number: { tone: "warning" },
}

function useDispositionLabel(disposition: CallDisposition): string {
  const { t } = useLingui()
  const labels: Record<CallDisposition, string> = {
    connected: t`Connected`,
    no_answer: t`No answer`,
    voicemail: t`Left voicemail`,
    busy: t`Busy`,
    wrong_number: t`Wrong number`,
  }
  return labels[disposition]
}

function isDisposition(value: string | null): value is CallDisposition {
  return value != null && (DISPOSITIONS as string[]).includes(value)
}

// Whether the customer actually connected — the mechanical
// fallback when the server hasn't stamped a disposition yet.
function wasAnswered(voiceCall: VoiceCallRecord): boolean {
  return voiceCall.answeredAt != null || (voiceCall.durationSeconds ?? 0) > 0
}

function initialDisposition(voiceCall: VoiceCallRecord): CallDisposition {
  if (isDisposition(voiceCall.disposition)) return voiceCall.disposition
  return wasAnswered(voiceCall) ? "connected" : "no_answer"
}

// Fire an immediate write (PATCH/POST) and toast a humane error
// on failure — never silently drop a rep's edit (CON-197 §A5).
// On success, runs the optional `onOk` (typically a partial
// reload so other surfaces reconcile). `errorMsg` is a
// translated string supplied by the caller (so this plain
// function doesn't need useLingui()).
function writeJson(
  url: string,
  method: "PATCH" | "POST",
  body: Record<string, unknown>,
  errorMsg: string,
  onOk?: () => void,
) {
  void fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify(body),
    credentials: "same-origin",
  })
    .then((response) => {
      if (!response.ok) {
        toast.error(errorMsg)
        return
      }
      onOk?.()
    })
    .catch(() => {
      toast.error(errorMsg)
    })
}

type Subview = "draft" | "more" | "whatsapp" | "linkDeal" | "transcript"

export function AfterCallCard({
  voiceCall: initial,
  seedNote = "",
  onClose,
  initialSubview = "draft",
  whatsappPreview = null,
  currentEmployee = FALLBACK_EMPLOYEE,
  ownerOptions = [],
}: AfterCallCardProps) {
  const { t } = useLingui()
  // Live-update from the post-call stream so the AI summary,
  // refined disposition, and follow-up suggestions can land
  // after the card has already mounted.
  const voiceCall = useVoiceCallChannel(initial)
  // The deal the call is logged against. Three states, so a rep
  // can override the AI's derived deal in either direction
  // (CON-170):
  //   undefined → untouched: follow the call's AI-derived deal
  //               (conversation/contact), even as a late
  //               insights pass refines it.
  //   null      → the rep explicitly cleared the deal.
  //   {deal}    → the rep explicitly picked / confirmed one.
  // A bare `?? voiceCall.deal` couldn't represent "cleared" —
  // clearing would just snap back to the AI's guess.
  const [linkedDeal, setLinkedDeal] = useState<
    VoiceCallDeal | null | undefined
  >(undefined)
  const deal = linkedDeal === undefined ? (voiceCall.deal ?? null) : linkedDeal
  // The rep took an explicit deal action (picked, confirmed, or
  // cleared). Until then a shown deal is still just the AI's
  // guess — which gates the stage-advance opt-in: we never nudge
  // the stage of a deal the rep didn't confirm.
  const dealConfirmedByRep = linkedDeal !== undefined
  const contactName = voiceCall.contact?.name ?? "your contact"
  const answered = wasAnswered(voiceCall)

  const [disposition, setDisposition] = useState<CallDisposition>(() =>
    initialDisposition(initial),
  )
  const [dispositionConfirmed, setDispositionConfirmed] = useState(false)
  const [dispositionOpen, setDispositionOpen] = useState(false)

  const [note, setNote] = useState(seedNote)
  const [noteEditing, setNoteEditing] = useState(false)
  const [noteDeleted, setNoteDeleted] = useState(false)
  // The rep typed before the AI summary landed — guards the
  // wipe-race: a late summary offers a swap, never a clobber.
  const noteEditedRef = useRef(seedNote.trim().length > 0)
  const [pendingSwap, setPendingSwap] = useState(false)
  // The note we'd restore if the rep undoes a "Use AI summary".
  const preSwapNoteRef = useRef<string | null>(null)
  const [swapped, setSwapped] = useState(false)
  // The note text the server already holds, so a Done-flush or a
  // blur doesn't re-PATCH an unchanged note. Seeds from the
  // existing summary note (the in-call jot is NOT yet persisted,
  // so it stays writable on first blur).
  const lastSavedNoteRef = useRef<string>(initial.summaryNote?.content ?? "")

  const [visibility, setVisibility] = useState<NoteVisibility>("team")
  const [advanceStage, setAdvanceStage] = useState(false)

  const [subview, setSubview] = useState<Subview>(initialSubview)
  // Where the Link-a-deal sub-view was opened from, so its
  // back / done returns there — the card trigger or the More
  // sheet ("respect where you came from").
  const [linkDealOrigin, setLinkDealOrigin] = useState<"draft" | "more">(
    "draft",
  )

  const hasSummary = !!voiceCall.summary
  const analysisFailed =
    voiceCall.transcriptStatus === "failed" ||
    voiceCall.insightsStatus === "failed"
  // Server-computed gate (`VoiceCall#analysis_in_flight?`,
  // CON-198). The `?? true` keeps the card's historical
  // null-tolerance: this card mounts right at call end, where
  // a broadcast minted before the flag shipped (mid-deploy) —
  // like a record with no statuses at all — should read as
  // "analysis is coming", not as a dead card.
  const analyzing =
    !hasSummary &&
    !analysisFailed &&
    (voiceCall.analysisInFlight ?? true)

  // Prefill the note with the AI summary when it lands —
  // unless the rep already typed, in which case offer a swap.
  useEffect(() => {
    if (!voiceCall.summary || noteDeleted) return
    if (noteEditedRef.current) {
      if (note.trim() !== voiceCall.summary.trim()) setPendingSwap(true)
    } else {
      setNote(voiceCall.summary)
      // The AI summary is already the server's saved note — sync
      // the baseline so a no-op blur doesn't re-PATCH it.
      lastSavedNoteRef.current = voiceCall.summary
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceCall.summary])

  // The AI insights pass refines the disposition (voicemail /
  // busy / wrong_number). Follow it until the rep takes over.
  useEffect(() => {
    if (dispositionConfirmed) return
    if (isDisposition(voiceCall.disposition)) {
      setDisposition(voiceCall.disposition)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceCall.disposition])

  const meta = DISPOSITION_META[disposition]
  const dispositionLabel = useDispositionLabel(disposition)
  const showAiDetected = voiceCall.dispositionInferred && !dispositionConfirmed

  // ── Immediate writes (CON-197 §A5) ──────────────────────
  // Note, disposition, deal-link, visibility, and stage-advance
  // each persist the moment they change — no batch commit on
  // Done. A write failure toasts a Sonner error and never
  // silently drops the rep's edit.
  const persistNote = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      // Skip an unchanged note, and skip a blank note (the
      // summary_note endpoint requires content; an empty note is
      // a Delete, handled by the More sheet's Delete affordance).
      if (trimmed === lastSavedNoteRef.current.trim()) return
      if (trimmed.length === 0) return
      lastSavedNoteRef.current = trimmed
      writeJson(
        `/v2/voice_calls/${voiceCall.id}/summary_note`,
        "PATCH",
        { content: trimmed },
        t`Couldn't save — please try again.`,
        () => router.reload({ only: ["activity", "timeline", "drawer"] }),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [voiceCall.id, t],
  )

  const changeNote = useCallback((value: string) => {
    noteEditedRef.current = true
    setNote(value)
  }, [])

  const useAiSummary = useCallback(() => {
    if (!voiceCall.summary) return
    preSwapNoteRef.current = note
    setNote(voiceCall.summary)
    setPendingSwap(false)
    setSwapped(true)
    persistNote(voiceCall.summary)
  }, [voiceCall.summary, note, persistNote])

  const undoSwap = useCallback(() => {
    if (preSwapNoteRef.current != null) {
      setNote(preSwapNoteRef.current)
      persistNote(preSwapNoteRef.current)
    }
    setSwapped(false)
    // Re-offer the swap — undo isn't a one-way door. The rep
    // can flip back to the AI summary after restoring their
    // note. (preSwapNoteRef is re-set on the next swap.)
    setPendingSwap(true)
  }, [persistNote])

  // Disposition → immediate PATCH (sets disposition_inferred
  // false server-side: the rep made the call, not the AI).
  const chooseDisposition = useCallback(
    (value: CallDisposition) => {
      setDisposition(value)
      setDispositionConfirmed(true)
      setDispositionOpen(false)
      writeJson(
        `/v2/voice_calls/${voiceCall.id}/disposition`,
        "PATCH",
        { disposition: value },
        t`Couldn't save — please try again.`,
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [voiceCall.id, t],
  )

  // Deal-link → immediate PATCH (the panel uses the same
  // endpoint). null clears the link.
  const persistDeal = useCallback(
    (dealId: number | null) => {
      writeJson(
        `/v2/voice_calls/${voiceCall.id}/deal`,
        "PATCH",
        { deal_id: dealId },
        t`Couldn't save — please try again.`,
        () => router.reload({ only: ["activity", "timeline", "drawer"] }),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [voiceCall.id, t],
  )

  // Rep picked / created a deal in the picker. Their choice wins
  // over the AI's guess; reset the stage-advance opt-in since it
  // was decided against a different deal.
  const pickDeal = useCallback(
    (picked: VoiceCallDeal) => {
      setLinkedDeal(picked)
      setAdvanceStage(false)
      setSubview(linkDealOrigin)
      persistDeal(picked.id)
    },
    [linkDealOrigin, persistDeal],
  )

  // Rep cleared the linked deal (unlink). Follow-ups fall back
  // to the contact.
  const clearDeal = useCallback(() => {
    setLinkedDeal(null)
    setAdvanceStage(false)
    setSubview(linkDealOrigin)
    persistDeal(null)
  }, [linkDealOrigin, persistDeal])

  // Rep confirmed the AI-derived deal as-is (from the More
  // sheet). Promotes the guess to a rep-confirmed pick (which
  // unlocks the stage-advance opt-in) and pins the link.
  const confirmDeal = useCallback(() => {
    if (deal) {
      setLinkedDeal(deal)
      persistDeal(deal.id)
    }
  }, [deal, persistDeal])

  // Note visibility → immediate write. NEEDS BACKEND: the
  // summary_note UpdateForm currently permits `content` only.
  // The orchestrator extends it (and the controller's
  // form_params) to accept `visibility`.
  const changeVisibility = useCallback(
    (next: NoteVisibility) => {
      setVisibility(next)
      writeJson(
        `/v2/voice_calls/${voiceCall.id}/summary_note`,
        "PATCH",
        { visibility: next },
        t`Couldn't save — please try again.`,
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [voiceCall.id, t],
  )

  // Stage-advance → immediate write. NEEDS BACKEND: a nested
  // resource that advances the call's deal to the next stage,
  // e.g. POST /v2/deals/:deal_id/stage_advances. Only ever fired
  // for a rep-confirmed deal.
  const toggleAdvanceStage = useCallback(() => {
    if (!deal || !dealConfirmedByRep) return
    const next = !advanceStage
    setAdvanceStage(next)
    if (next) {
      writeJson(
        `/v2/deals/${deal.id}/stage_advances`,
        "POST",
        { voice_call_id: voiceCall.id },
        t`Couldn't save — please try again.`,
        () => router.reload({ only: ["activity", "timeline", "drawer"] }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal, dealConfirmedByRep, advanceStage, voiceCall.id, t])

  // Done is a pure close — every decision (note, disposition,
  // deal-link, follow-up Accept/Reject) already persisted the
  // moment it happened. Flush any pending note edit (a blur the
  // rep didn't trigger by clicking away) before closing so an
  // in-progress note isn't lost.
  const handleDone = useCallback(() => {
    if (!noteDeleted) persistNote(note)
    onClose()
  }, [noteDeleted, note, persistNote, onClose])

  const regenerate = useCallback(() => {
    setSubview("draft")
    void fetch(`/v2/channels/voice/calls/${voiceCall.id}/insights`, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
      credentials: "same-origin",
    })
      .then((response) => {
        if (response.ok) {
          toast.success(t`Regenerating the summary…`)
        } else {
          toast.error(t`We couldn't regenerate that. Please try again.`)
        }
      })
      .catch(() => {
        toast.error(t`We couldn't regenerate that. Please try again.`)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceCall.id, t])

  const deleteNote = useCallback(() => {
    void fetch(`/v2/channels/voice/calls/${voiceCall.id}/note`, {
      method: "DELETE",
      headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          toast.error(t`We couldn't delete that. Please try again.`)
          return
        }
        // The AI draft is gone; keep the rep in the card with an
        // empty note rather than inventing a post-delete state.
        noteEditedRef.current = true
        setNote("")
        lastSavedNoteRef.current = ""
        setNoteEditing(false)
        setNoteDeleted(true)
        setPendingSwap(false)
        setSwapped(false)
        setSubview("draft")
        toast.success(t`Note deleted`)
      })
      .catch(() => {
        toast.error(t`We couldn't delete that. Please try again.`)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceCall.id, t])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault()
      if (subview !== "draft") setSubview("draft")
      else onClose()
      return
    }
    if (subview !== "draft") return
    if (event.key === "Enter" && !event.shiftKey) {
      const target = event.target as HTMLElement | null
      if (
        target?.closest(
          "button,input,textarea,select,a,[role=checkbox],[role=button]",
        )
      ) {
        return
      }
      event.preventDefault()
      handleDone()
    }
  }

  // Header subtitle: a connected call shows its length; a
  // no-answer shows how long it rang. Derived from the LIVE
  // record, not the mount-time prop — the card can mount before
  // the terminal broadcast reaches it, and a late `call.ended`
  // sync must correct a stale "No answer" (CON-211).
  const ringSeconds =
    voiceCall.startedAt && voiceCall.endedAt
      ? Math.max(
          0,
          Math.round(
            (new Date(voiceCall.endedAt).getTime() -
              new Date(voiceCall.startedAt).getTime()) /
              1000,
          ),
        )
      : null
  const subtitle = answered
    ? t`Call ended · ${formatDuration(voiceCall.durationSeconds ?? 0)}`
    : ringSeconds != null
      ? t`No answer · rang ${ringSeconds}s`
      : t`No answer`

  return (
    <div
      data-testid="after-call-card"
      role="group"
      aria-label={t`After call`}
      onKeyDown={handleKeyDown}
      className="text-[#FBF7F0]"
    >
      {subview === "more" ? (
        <AfterCallMoreSheet
          deal={deal}
          dealConfirmedByRep={dealConfirmedByRep}
          onLinkDeal={() => {
            setLinkDealOrigin("more")
            setSubview("linkDeal")
          }}
          onChangeDeal={() => {
            setLinkDealOrigin("more")
            setSubview("linkDeal")
          }}
          onConfirmDeal={confirmDeal}
          visibility={visibility}
          onVisibilityChange={changeVisibility}
          advanceStage={advanceStage}
          onToggleAdvanceStage={toggleAdvanceStage}
          onRegenerate={regenerate}
          canDeleteNote={hasSummary && !noteDeleted}
          onDeleteNote={deleteNote}
          onClose={() => setSubview("draft")}
        />
      ) : subview === "whatsapp" ? (
        <WhatsAppFollowUp
          voiceCall={voiceCall}
          contactName={contactName}
          onBack={() => setSubview("draft")}
          onSent={onClose}
          previewData={whatsappPreview}
        />
      ) : subview === "linkDeal" ? (
        <LinkDeal
          voiceCall={voiceCall}
          contactName={contactName}
          currentDeal={deal}
          pickTitle={deal ? t`Change deal` : t`Link a deal`}
          onBack={() => setSubview(linkDealOrigin)}
          onLink={pickDeal}
          onClear={deal ? clearDeal : undefined}
        />
      ) : subview === "transcript" ? (
        <AfterCallTranscript
          call={voiceCall}
          onBack={() => setSubview("draft")}
        />
      ) : (
        <div className="flex flex-col gap-3.5 px-[18px] pb-4 pt-[18px]">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="flex size-[38px] shrink-0 items-center justify-center rounded-full bg-[#3A352E] text-[13px] font-semibold text-[#F0EDE6]">
              {initials(contactName)}
            </div>
            <div className="flex min-w-0 grow flex-col gap-0.5">
              <span className="truncate text-sm font-semibold text-[#FBF7F0]">
                {contactName}
              </span>
              <span className="truncate text-xs text-[#9A938A]">
                {subtitle}
              </span>
            </div>
            <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#22361F] px-[9px] py-1">
              <CheckIcon width={11} height={11} className="text-[#7FD79A]" />
              <span className="text-[11px] font-medium text-[#7FD79A]">
                <Trans>Logged</Trans>
              </span>
            </span>
          </div>

          {/* AI summary block */}
          <div className="flex flex-col gap-2.5 rounded-[13px] bg-[#2A2620] px-3.5 py-[13px]">
            {analyzing ? (
              <AnalyzingBlock
                note={note}
                onNoteChange={changeNote}
                onNoteBlur={() => persistNote(note)}
              />
            ) : (
              <>
                {pendingSwap ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <span className="grow text-[11px] font-semibold uppercase tracking-[0.04em] text-[#9A938A]">
                        <Trans>Your note</Trans>
                      </span>
                      <button
                        type="button"
                        onClick={useAiSummary}
                        data-testid="after-call-use-ai-summary"
                        className="flex items-center gap-1.5 rounded-full bg-[#3A2F1E] px-2.5 py-1"
                      >
                        <SparkleIcon
                          width={11}
                          height={11}
                          className="text-[#E0A56A]"
                        />
                        <span className="text-[11px] font-semibold text-[#E0A56A]">
                          <Trans>Use AI summary</Trans>
                        </span>
                      </button>
                    </div>
                    {/* Preview the AI draft so the rep sees what
                        they'd adopt before swapping — read-only,
                        non-destructive. */}
                    {voiceCall.summary ? (
                      <div
                        data-testid="after-call-ai-preview"
                        className="rounded-[9px] border border-[#3A352E] bg-[#221E18] px-[11px] py-2"
                      >
                        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.04em] text-[#E0A56A]">
                          <Trans>AI summary</Trans>
                        </span>
                        <span className="block text-[12px] leading-[17px] text-[#9A938A]">
                          {voiceCall.summary}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : hasSummary && !noteDeleted ? (
                  <div className="flex items-center gap-1.5">
                    <SparkleIcon
                      width={12}
                      height={12}
                      className="text-[#E0A56A]"
                    />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#E0A56A]">
                      <Trans>AI drafted from the call</Trans>
                    </span>
                  </div>
                ) : null}

                {/* Note body: read-paragraph (tap to edit) or
                    an open textarea when editing / empty. The
                    disposition lives in the chip below — no
                    separate headline (it just duplicated it). */}
                <NoteBody
                  note={note}
                  editing={noteEditing}
                  emptyPrompt={
                    noteDeleted
                      ? t`Add a note for this call…`
                      : t`What came out of this call?`
                  }
                  showReset={hasSummary && !noteDeleted}
                  onStartEditing={() => setNoteEditing(true)}
                  onStopEditing={() => {
                    setNoteEditing(false)
                    // Save on blur — the note follows the same
                    // "immediate writes" model as the panel
                    // (CON-197 §A5).
                    persistNote(note)
                  }}
                  onChange={changeNote}
                  onReset={() => {
                    if (voiceCall.summary) setNote(voiceCall.summary)
                  }}
                />

                {swapped ? (
                  <button
                    type="button"
                    onClick={undoSwap}
                    data-testid="after-call-swap-undo"
                    className="self-start text-[11px] font-medium text-[#9A938A] underline"
                  >
                    <Trans>Undo — restore your note</Trans>
                  </button>
                ) : null}

                {/* Disposition chip / inline dropdown */}
                {dispositionOpen ? (
                  <DispositionDropdown
                    value={disposition}
                    onChoose={chooseDisposition}
                  />
                ) : (
                  <div className="flex items-center gap-2 pt-0.5">
                    <span
                      className="flex items-center gap-1.5 rounded-full px-2.5 py-1"
                      style={{
                        backgroundColor:
                          meta.tone === "connected" ? "#22361F" : "#33301F",
                      }}
                    >
                      <span
                        aria-hidden="true"
                        className="size-1.5 shrink-0 rounded-full"
                        style={{
                          backgroundColor:
                            meta.tone === "connected" ? "#7FD79A" : "#C9A24A",
                        }}
                      />
                      <span
                        className="text-[11px] font-semibold"
                        style={{
                          color:
                            meta.tone === "connected" ? "#7FD79A" : "#E0C68A",
                        }}
                      >
                        {dispositionLabel}
                      </span>
                    </span>
                    {/* "Change" sits right beside the chip so it
                        clearly changes the disposition — not the
                        note (it read as "edit note" out on the
                        right edge). */}
                    <button
                      type="button"
                      onClick={() => setDispositionOpen(true)}
                      data-testid="after-call-change-disposition"
                      className="text-[11px] font-medium text-[#9A938A] underline"
                    >
                      <Trans>Change</Trans>
                    </button>
                    {showAiDetected ? (
                      <span className="text-[11px] text-[#6E685F]">
                        <Trans>AI-detected</Trans>
                      </span>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>

          {/* View transcript — grounds the AI write-up above.
              Only when there are ready turns to show. */}
          {voiceCall.transcript.length > 0 &&
          voiceCall.transcriptStatus === "ready" ? (
            <button
              type="button"
              onClick={() => setSubview("transcript")}
              data-testid="after-call-view-transcript"
              className="self-start text-[13px] font-medium text-[#E0A56A] hover:underline"
            >
              <Trans>View transcript</Trans>
            </button>
          ) : null}

          {/* Follow-ups — the shared "AI proposes, human
              approves" section. Accept / Reject / kebab edit
              each write immediately (CON-197 §A5). */}
          {!analyzing ? (
            <CallFollowUps
              voiceCall={voiceCall}
              theme="dark"
              currentEmployee={currentEmployee}
              ownerOptions={ownerOptions}
            />
          ) : null}

          {/* No-deal path (frame 16): a quiet, optional link to
              a deal — never the old "needs a deal" dead-end.
              Follow-ups already target the contact. */}
          {!analyzing && !deal ? (
            <button
              type="button"
              onClick={() => {
                setLinkDealOrigin("draft")
                setSubview("linkDeal")
              }}
              data-testid="after-call-link-deal-trigger"
              className="flex items-center gap-[7px] self-start"
            >
              <PlusIcon width={13} height={13} className="text-[#E0A56A]" />
              <span className="text-xs font-medium text-[#E0A56A]">
                <Trans>Link a deal</Trans>
              </span>
              <span className="text-xs text-[#6E685F]">
                <Trans>optional</Trans>
              </span>
            </button>
          ) : null}

          {/* No-answer pivot: nudge a WhatsApp message instead */}
          {disposition === "no_answer" ? (
            <button
              type="button"
              onClick={() => setSubview("whatsapp")}
              data-testid="after-call-whatsapp-follow-up"
              className="flex items-center justify-center gap-[7px] rounded-[11px] border border-[#3A352E] p-[9px] text-[13px] font-medium text-[#D9D3C8] hover:bg-[#2A2620]"
            >
              <ChatBubbleIcon
                width={14}
                height={14}
                className="text-[#7FD79A]"
              />
              <Trans>Message on WhatsApp</Trans>
            </button>
          ) : null}

          {/* Footer — Done is a pure close; everything already
              saved as the rep made each decision (CON-197 §A5). */}
          <div className="flex items-center gap-[9px]">
            <button
              type="button"
              onClick={handleDone}
              data-testid="after-call-done"
              className="flex grow items-center justify-center rounded-[11px] bg-[#F4EFE6] px-3.5 py-2.5 text-[13px] font-semibold text-[#1E1B17] hover:bg-[#FBF7F0]"
            >
              <Trans>Done</Trans>
            </button>
            <button
              type="button"
              onClick={() => setSubview("more")}
              data-testid="after-call-more-button"
              className="flex items-center justify-center rounded-[11px] border border-[#3A352E] px-3.5 py-2.5 text-[13px] font-medium text-[#D9D3C8] hover:bg-[#2A2620]"
            >
              <Trans>More</Trans>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Analyzing state (frame 2): a shimmer where the summary will
// land — never an empty box — plus a writable note field so the
// rep can type now or wait.
function AnalyzingBlock({
  note,
  onNoteChange,
  onNoteBlur,
}: {
  note: string
  onNoteChange: (value: string) => void
  onNoteBlur: () => void
}) {
  const { t } = useLingui()
  return (
    <>
      <div className="flex items-center gap-1.5">
        <SparkleIcon width={12} height={12} className="text-[#E0A56A]" />
        <span
          data-testid="after-call-analyzing"
          className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#E0A56A]"
        >
          <Trans>Analyzing call…</Trans>
        </span>
      </div>
      <div
        aria-hidden="true"
        className="flex flex-col gap-[7px]"
        data-testid="after-call-shimmer"
      >
        <div className="h-2 w-full animate-pulse rounded-sm bg-[#3A352E]" />
        <div className="h-2 w-[85%] animate-pulse rounded-sm bg-[#3A352E]" />
        <div className="h-2 w-[60%] animate-pulse rounded-sm bg-[#3A352E]" />
      </div>
      <div className="flex flex-col gap-[5px] pt-[3px]">
        {/* The note is the rep's own — the AI drafts its summary
            separately from the transcript; you can swap the AI's
            in once it lands. Don't imply the note "feeds" the AI. */}
        <span className="text-[11px] text-[#9A938A]">
          <Trans>Your note · or use the AI summary once it lands</Trans>
        </span>
        <textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          onBlur={onNoteBlur}
          onKeyDown={stopEnterEscape}
          aria-label={t`Call note`}
          placeholder={t`Add a quick note…`}
          rows={2}
          className="resize-none rounded-[9px] border border-[#3A352E] bg-transparent px-[11px] py-[9px] text-[13px] text-[#FBF7F0] placeholder:text-[#6E685F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A56A]/40"
        />
      </div>
    </>
  )
}

// The note body: a read-mode paragraph the rep taps to edit in
// place (frame 15), or an always-open textarea when there's no
// text yet. One field, never two stacked.
function NoteBody({
  note,
  editing,
  emptyPrompt,
  showReset,
  onStartEditing,
  onStopEditing,
  onChange,
  onReset,
}: {
  note: string
  editing: boolean
  emptyPrompt: string
  showReset: boolean
  onStartEditing: () => void
  onStopEditing: () => void
  onChange: (value: string) => void
  onReset: () => void
}) {
  const { t } = useLingui()
  const open = editing || note.trim().length === 0

  if (!open) {
    return (
      <button
        type="button"
        onClick={onStartEditing}
        data-testid="after-call-note-read"
        className="text-left text-[13px] leading-[19px] text-[#D9D3C8] hover:text-[#FBF7F0]"
      >
        {note}
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-[7px]">
      <textarea
        value={note}
        autoFocus={editing}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        onBlur={onStopEditing}
        onKeyDown={stopEnterEscape}
        aria-label={t`Call note`}
        placeholder={emptyPrompt}
        rows={3}
        data-testid="after-call-note-edit"
        className="resize-none rounded-[9px] border-[1.5px] border-[#E0A56A] bg-[#221E18] px-[11px] py-[9px] text-[13px] leading-[19px] text-[#FBF7F0] placeholder:text-[#6E685F] focus:outline-none"
      />
      {showReset ? (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            // Keep the textarea focused so resetting swaps the
            // text in place instead of blurring → closing the
            // editor (it read as "the editor just closed").
            onMouseDown={(e) => e.preventDefault()}
            onClick={onReset}
            data-testid="after-call-reset-summary"
            className="flex items-center gap-1.5 text-[11px] font-medium text-[#9A938A] underline"
          >
            <ResetIcon width={11} height={11} />
            <Trans>Reset to AI summary</Trans>
          </button>
          <span className="text-[11px] text-[#6E685F]">
            <Trans>· edits save to the note</Trans>
          </span>
        </div>
      ) : null}
    </div>
  )
}

// The inline disposition dropdown (frame 6): the five options
// with the current one ticked. Replaces the chip while open.
function DispositionDropdown({
  value,
  onChoose,
}: {
  value: CallDisposition
  onChoose: (value: CallDisposition) => void
}) {
  const { t } = useLingui()
  return (
    <div className="flex flex-col gap-[7px] pt-0.5">
      <span className="text-[11px] text-[#9A938A]">
        <Trans>Outcome — change if the AI got it wrong</Trans>
      </span>
      <div
        role="listbox"
        aria-label={t`Call outcome`}
        className="flex flex-col gap-[3px] rounded-[10px] border border-[#3A352E] bg-[#252019] p-[5px]"
      >
        {DISPOSITIONS.map((key) => {
          const active = key === value
          const optionMeta = DISPOSITION_META[key]
          return (
            <DispositionOption
              key={key}
              dispositionKey={key}
              active={active}
              tone={optionMeta.tone}
              onChoose={onChoose}
            />
          )
        })}
      </div>
    </div>
  )
}

function DispositionOption({
  dispositionKey,
  active,
  tone,
  onChoose,
}: {
  dispositionKey: CallDisposition
  active: boolean
  tone: "connected" | "warning"
  onChoose: (value: CallDisposition) => void
}) {
  const label = useDispositionLabel(dispositionKey)
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={() => onChoose(dispositionKey)}
      className={`flex items-center gap-2 rounded-[7px] px-[9px] py-[7px] text-left text-xs ${
        active ? "bg-[#2F2A24]" : ""
      }`}
    >
      {active ? (
        <span
          aria-hidden="true"
          className="size-1.5 shrink-0 rounded-full"
          style={{
            backgroundColor: tone === "connected" ? "#7FD79A" : "#C9A24A",
          }}
        />
      ) : null}
      <span
        className={`grow ${
          active ? "font-semibold text-[#FBF7F0]" : "text-[#D9D3C8]"
        }`}
      >
        {label}
      </span>
      {active ? (
        <CheckIcon width={12} height={12} className="text-[#7FD79A]" />
      ) : null}
    </button>
  )
}


// Keep Enter/Esc local to a text field — Enter inserts a
// newline, Esc shouldn't dismiss the card mid-type.
function stopEnterEscape(e: KeyboardEvent<HTMLElement>) {
  if (e.key === "Enter" || e.key === "Escape") e.stopPropagation()
}
